import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { adminAPI, documentAPI } from "../services/api";
import {
  clearSession,
  isSessionExpired,
  millisUntilExpiry,
  redirectToLogin,
} from "../services/auth";
import { DOCUMENT_TYPE_OPTIONS } from "../types";
import type {
  AdminDocument,
  AdminUpdateRequest,
  RateLimitConfig,
  SearchResultLimitConfig,
} from "../types";
import Select from "../components/Select";
import toast from "react-hot-toast";

const PAGE_SIZE = 15;

const AdminDashboardPage: React.FC = () => {
  const [documents, setDocuments] = useState<AdminDocument[]>([]);
  const [loading, setLoading] = useState(true);
  // Currently signed-in admin (from localStorage "admin_user")
  const [adminUser, setAdminUser] = useState<{
    username?: string;
    role?: string;
  } | null>(null);
  // Admin badge dropdown (holds the Logout action)
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [showReviewOnly, setShowReviewOnly] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<AdminDocument | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<AdminUpdateRequest>({});
  const [uploading, setUploading] = useState(false);

  // Approve-for-public confirmation modal
  const [approveTarget, setApproveTarget] = useState<AdminDocument | null>(null);
  const [approving, setApproving] = useState(false);

  // Delete-document confirmation modal
  const [deleteTarget, setDeleteTarget] = useState<AdminDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Id of the document currently being downloaded (null when idle)
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Rate-limit configuration modal
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState<RateLimitConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configInput, setConfigInput] = useState("");
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSaved, setConfigSaved] = useState(false);

  // Extraction-confidence threshold configuration (shares the config modal).
  // Stored as a fraction 0.0–1.0 in Cosmos; shown/edited here as a percentage.
  const [confidenceScore, setConfidenceScore] = useState<number | null>(null);
  const [confidenceInput, setConfidenceInput] = useState("");
  const [confidenceSaving, setConfidenceSaving] = useState(false);
  const [confidenceError, setConfidenceError] = useState<string | null>(null);
  const [confidenceSaved, setConfidenceSaved] = useState(false);

  // Fixed cap on results returned by relevancy-ranked public search
  // (shares the config modal). Backend accepts 1–10,000.
  const [searchLimit, setSearchLimit] = useState<SearchResultLimitConfig | null>(
    null,
  );
  const [searchLimitInput, setSearchLimitInput] = useState("");
  const [searchLimitSaving, setSearchLimitSaving] = useState(false);
  const [searchLimitError, setSearchLimitError] = useState<string | null>(null);
  const [searchLimitSaved, setSearchLimitSaved] = useState(false);

  // Which configuration accordion panel is expanded.
  const [openSection, setOpenSection] = useState<
    "rate" | "confidence" | "searchLimit" | null
  >("rate");

  // Search / filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAgency, setFilterAgency] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterPii, setFilterPii] = useState<"" | "yes" | "no">("");
  const [filterStatus, setFilterStatus] = useState<"" | "review" | "reviewed">("");

  // Sort state
  const [sortKey, setSortKey] = useState<keyof AdminDocument>("upload_date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Pagination
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    loadDocuments();
  }, [showReviewOnly]);
  useEffect(() => {
    const expireNow = () => redirectToLogin({ expired: true });

    if (isSessionExpired()) {
      expireNow();
      return;
    }

    const timer = window.setTimeout(expireNow, millisUntilExpiry());

    const recheck = () => {
      if (document.visibilityState === "visible" && isSessionExpired()) {
        expireNow();
      }
    };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
    };
  }, []);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [
    searchQuery,
    filterAgency,
    filterType,
    filterYear,
    filterPii,
    filterStatus,
    showReviewOnly,
  ]);

  // Close the admin badge dropdown on outside click or Escape.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [userMenuOpen]);

  const checkAuth = async () => {
    if (isSessionExpired()) {
      redirectToLogin({ expired: true });
      return;
    }
    try {
      const raw = localStorage.getItem("admin_user");
      if (raw) {
        setAdminUser(JSON.parse(raw));
        return;
      }
      const data = await adminAPI.getCurrentUser();
      if (data?.user) {
        setAdminUser(data.user);
        localStorage.setItem("admin_user", JSON.stringify(data.user));
      }
    } catch {
      // Non-fatal: header just omits the user chip if this fails.
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDocumentsForReview(
        0,
        500,
        showReviewOnly,
      );
      setDocuments(response.documents);
    } catch (err) {
      console.error("Failed to load documents", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearSession();
    navigate("/admin");
  };

  // Open the rate-limit configuration modal and load current values.
  const openConfig = async () => {
    setShowConfig(true);
    setConfigError(null);
    setConfigSaved(false);
    setConfidenceError(null);
    setConfidenceSaved(false);
    setSearchLimitError(null);
    setSearchLimitSaved(false);
    try {
      setConfigLoading(true);
      const [cfg, score, limit] = await Promise.all([
        adminAPI.getRateLimitConfig(),
        adminAPI.getConfidenceScore().catch(() => null),
        // Tolerate a failure here so one unavailable config can't blank out the
        // whole modal; the panel reports its own missing value.
        adminAPI.getSearchResultLimitConfig().catch(() => null),
      ]);
      setConfig(cfg);
      setConfigInput(String(cfg.max_requests));
      setConfidenceScore(score);
      // Stored as a 0.0–1.0 fraction; edited here as a whole-number percentage.
      setConfidenceInput(score != null ? String(Math.round(score * 100)) : "");
      setSearchLimit(limit);
      setSearchLimitInput(limit != null ? String(limit.max_results) : "");
    } catch {
      setConfigError("Failed to load rate-limit configuration.");
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    const value = parseInt(configInput, 10);
    if (!Number.isInteger(value) || value < 1) {
      setConfigError("Please enter a whole number greater than or equal to 1.");
      return;
    }
    try {
      setConfigSaving(true);
      setConfigError(null);
      const updated = await adminAPI.updateRateLimitConfig(value);
      setConfig(updated);
      setConfigInput(String(updated.max_requests));
      setConfigSaved(true);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        "Failed to update the rate limit. Please try again.";
      setConfigError(detail);
    } finally {
      setConfigSaving(false);
    }
  };

  // Persist the extraction-confidence threshold. Entered as a percentage,
  // stored as a 0.0–1.0 fraction. Takes effect on the next document upload.
  const handleSaveConfidence = async () => {
    const pct = parseFloat(confidenceInput);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      setConfidenceError("Please enter a percentage between 0 and 100.");
      return;
    }
    try {
      setConfidenceSaving(true);
      setConfidenceError(null);
      const saved = await adminAPI.updateConfidenceScore(pct / 100);
      setConfidenceScore(saved);
      setConfidenceInput(String(Math.round(saved * 100)));
      setConfidenceSaved(true);
    } catch (err: any) {
      const detail =
        err?.response?.data?.message ||
        "Failed to update the confidence threshold. Please try again.";
      setConfidenceError(detail);
    } finally {
      setConfidenceSaving(false);
    }
  };

  // Persist the fixed cap on relevancy-ranked public search results.
  // Bounds mirror the backend's SearchResultLimitConfigUpdate (1–10,000), so an
  // out-of-range value is caught here instead of coming back as a 422.
  const handleSaveSearchLimit = async () => {
    const value = parseInt(searchLimitInput, 10);
    if (!Number.isInteger(value) || value < 1 || value > 10000) {
      setSearchLimitError("Please enter a whole number between 1 and 10,000.");
      return;
    }
    try {
      setSearchLimitSaving(true);
      setSearchLimitError(null);
      const updated = await adminAPI.updateSearchResultLimitConfig(value);
      setSearchLimit(updated);
      setSearchLimitInput(String(updated.max_results));
      setSearchLimitSaved(true);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        "Failed to update the search result limit. Please try again.";
      setSearchLimitError(detail);
    } finally {
      setSearchLimitSaving(false);
    }
  };

  const handleEditDocument = (doc: AdminDocument) => {
    setSelectedDoc(doc);
    setEditData({
      reference_number: doc.reference_number,
      related_party: doc.related_party,
      subject_location: doc.subject_location,
      document_type: doc.document_type,
      document_year: doc.document_year,
    });
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedDoc) return;
    try {
      await adminAPI.updateDocumentMetadata(selectedDoc.id, editData);
      setEditMode(false);
      setSelectedDoc(null);
      loadDocuments();
    } catch {
      alert("Failed to update document");
    }
  };

  // Clicking the approve (tick) icon opens a confirmation modal instead of a
  // native browser confirm() dialog.
  const handleApproveForPublic = (doc: AdminDocument) => {
    setApproveTarget(doc);
  };

  const confirmApprove = async () => {
    if (!approveTarget) return;
    try {
      setApproving(true);
      await adminAPI.approveDocument(approveTarget.id);
      setApproveTarget(null);
      loadDocuments();
    } catch {
      alert("Failed to approve document");
    } finally {
      setApproving(false);
    }
  };

  // Clicking the delete (trash) icon opens a confirmation modal instead of a
  // native browser confirm() dialog.
  const handleDeleteDocument = (doc: AdminDocument) => {
    setDeleteTarget(doc);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await adminAPI.deleteDocument(deleteTarget.id);
      setDeleteTarget(null);
      loadDocuments();
      toast.success("Document deleted successfully");
    } catch {
      toast.error("Failed to delete document");
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadDocument = async (doc: AdminDocument) => {
    // Prevent duplicate downloads while one is already in flight for this row.
    if (downloadingId) return;
    const toastId = toast.loading("Download is in progress, please wait…");
    try {
      setDownloadingId(doc.id);
      // Stream the file from Azure Blob Storage and trigger a browser download.
      await documentAPI.downloadDocument(doc.id);
      toast.success("Download started", { id: toastId });
    } catch {
      toast.error("Failed to download document. Please try again.", {
        id: toastId,
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      await documentAPI.uploadDocument(file);
      alert("Document uploaded successfully!");
      loadDocuments();
    } catch {
      alert("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSort = (key: keyof AdminDocument) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  // Derive unique filter options from loaded data
  const agencyOptions = useMemo(
    () =>
      [
        ...new Set(documents.map((d) => d.related_party).filter(Boolean)),
      ].sort() as string[],
    [documents],
  );
  const yearOptions = useMemo(
    () =>
      [
        ...new Set(
          documents.map((d) => (d as any).document_year).filter(Boolean),
        ),
      ]
        .sort((a, b) => b - a)
        .map(String),
    [documents],
  );

  // Filter + sort pipeline (client-side)
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return documents.filter((d) => {
      if (q) {
        const hay = [
          d.reference_number,
          d.related_party,
          d.subject_location,
          d.original_file_name,
          d.file_name,
          d.document_type,
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterAgency && d.related_party !== filterAgency) return false;
      if (filterType && d.document_type !== filterType) return false;
      if (filterYear && String((d as any).document_year) !== filterYear)
        return false;
      if (filterPii === "yes" && !d.pii_detected) return false;
      if (filterPii === "no" && d.pii_detected) return false;
      if (filterStatus === "review" && !d.requires_review) return false;
      if (filterStatus === "reviewed" && d.requires_review) return false;
      return true;
    });
  }, [
    documents,
    searchQuery,
    filterAgency,
    filterType,
    filterYear,
    filterPii,
    filterStatus,
  ]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = (a as any)[sortKey] ?? "";
      const bv = (b as any)[sortKey] ?? "";
      const cmp = String(av).localeCompare(String(bv), undefined, {
        numeric: true,
      });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const SortIcon = ({ col }: { col: keyof AdminDocument }) => {
    if (sortKey !== col) return <span className="ml-1 text-merced-gray-300">↕</span>;
    return <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  const thClass =
    "px-2 py-3 text-left text-xs font-semibold text-merced-gray-600 uppercase tracking-wider cursor-pointer select-none hover:bg-merced-gray-100";
  const tdClass = "px-2 py-3 text-sm text-merced-gray-700 align-top";

  return (
    <div className="flex min-h-screen flex-col bg-merced-gray-50">
      {/* Thin government accent bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-merced-blue-700 via-merced-blue-500 to-merced-gold" />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white shadow-md border-b border-merced-gray-200 animate-fade-in-down">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center gap-3">
              <img
                src="https://www.cityofmerced.gov/Project/Contents/Main/_gfx/cmn/logo.svg"
                alt="City of Merced Logo"
                className="h-12 w-auto transition-transform duration-300 hover:scale-105"
              />
              <div className="hidden border-l border-merced-gray-200 pl-3 sm:block">
                <p className="text-base font-heading font-semibold leading-tight text-merced-blue-700">
                  Admin Dashboard
                </p>
                <p className="text-xs uppercase tracking-wider text-merced-gray-500">
                  Contract Document Management
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={openConfig}
                title="Configure the global hourly search rate limit"
                className="inline-flex items-center gap-1.5 rounded-lg border border-merced-blue-700 px-4 py-2 text-sm font-medium text-merced-blue-700 transition-all duration-200 hover:bg-merced-blue-700 hover:text-white hover:shadow-md"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Configuration
              </button>

              {/* Admin badge — click to reveal the Logout action */}
              {adminUser?.username && (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen((o) => !o)}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    title={`Signed in as ${adminUser.username}`}
                    className="inline-flex items-center gap-2 rounded-full border border-merced-gray-200 bg-white py-1.5 pl-1.5 pr-3 shadow-sm transition-all duration-200 hover:border-merced-blue-200 hover:shadow-md"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-merced-blue-100 text-sm font-semibold uppercase text-merced-blue-700">
                      {adminUser.username.charAt(0)}
                    </span>
                    <span className="hidden text-left leading-tight sm:block">
                      <span className="block max-w-[180px] truncate text-sm font-semibold text-merced-gray-800">
                        {adminUser.username}
                      </span>
                      <span className="block text-xs uppercase tracking-wider text-merced-gray-500">
                        {adminUser.role || "Admin"}
                      </span>
                    </span>
                    <svg
                      className={`h-4 w-4 text-merced-gray-400 transition-transform duration-200 ${
                        userMenuOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {userMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 z-50 mt-2 w-56 origin-top-right overflow-hidden rounded-lg border border-merced-gray-200 bg-white shadow-xl animate-scale-in"
                    >
                      <div className="border-b border-merced-gray-100 px-4 py-3">
                        <p className="truncate text-sm font-semibold text-merced-gray-800">
                          {adminUser.username}
                        </p>
                        <p className="text-xs uppercase tracking-wider text-merced-gray-500">
                          {adminUser.role || "Admin"}
                        </p>
                      </div>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-merced-rust-600 transition-colors hover:bg-merced-rust-50"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto w-full flex-1 px-4 py-6 animate-fade-in-up">
        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow-sm border border-merced-gray-200 p-4 mb-4">
          <div className="flex flex-col gap-4">
            {/* Row 1 — search + upload */}
            <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
              <div className="group relative flex-1 max-w-md">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-merced-gray-400 transition-colors duration-200 group-focus-within:text-merced-blue">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search contract number, agency, subject…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-elegant"
                  aria-label="Search documents"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-merced-gray-400 transition-colors hover:bg-merced-gray-100 hover:text-merced-gray-600"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-merced-gray-200 bg-white px-3 py-2 text-sm font-medium text-merced-gray-700 shadow-sm transition-colors hover:border-merced-blue-300 hover:text-merced-blue-700">
                  <input
                    type="checkbox"
                    checked={showReviewOnly}
                    onChange={(e) => setShowReviewOnly(e.target.checked)}
                    className="h-4 w-4 rounded border-merced-gray-300 text-merced-blue accent-merced-blue focus:ring-merced-blue"
                  />
                  Requires review only
                </label>

                <button
                  onClick={loadDocuments}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-merced-blue-700 px-4 py-2 text-sm font-medium text-merced-blue-700 transition-all duration-200 hover:bg-merced-blue-700 hover:text-white hover:shadow-md"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Refresh
                </button>
              </div>
            </div>

            {/* Row 2 — filter dropdowns */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-merced-gray-500">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 018 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
                  />
                </svg>
                Filters
              </span>

              <Select
                value={filterAgency}
                onChange={setFilterAgency}
                placeholder="All Agencies"
                ariaLabel="Filter by agency"
                className="min-w-[160px]"
                options={agencyOptions.map((a) => ({ value: a, label: a }))}
              />

              <Select
                value={filterType}
                onChange={setFilterType}
                placeholder="All Types"
                ariaLabel="Filter by document type"
                className="min-w-[160px]"
                options={DOCUMENT_TYPE_OPTIONS}
              />

              <Select
                value={filterYear}
                onChange={setFilterYear}
                placeholder="All Years"
                ariaLabel="Filter by year"
                className="min-w-[110px]"
                options={yearOptions.map((y) => ({ value: y, label: y }))}
              />

              <Select
                value={filterPii}
                onChange={(v) => setFilterPii(v as "" | "yes" | "no")}
                placeholder="PII: Any"
                ariaLabel="Filter by PII status"
                className="min-w-[130px]"
                options={[
                  { value: "yes", label: "PII: Detected" },
                  { value: "no", label: "PII: None" },
                ]}
              />

              <Select
                value={filterStatus}
                onChange={(v) =>
                  setFilterStatus(v as "" | "review" | "reviewed")
                }
                placeholder="All Statuses"
                ariaLabel="Filter by review status"
                className="min-w-[150px]"
                options={[
                  { value: "review", label: "Need to Review" },
                  { value: "reviewed", label: "Reviewed" },
                ]}
              />

              {(searchQuery ||
                filterAgency ||
                filterType ||
                filterYear ||
                filterPii ||
                filterStatus) && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setFilterAgency("");
                      setFilterType("");
                      setFilterYear("");
                      setFilterPii("");
                      setFilterStatus("");
                    }}
                    className="inline-flex items-center gap-1 self-center rounded-lg px-3 py-2 text-sm font-medium text-merced-rust-500 transition-colors hover:bg-merced-rust-50 hover:text-merced-rust-700"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Clear filters
                  </button>
                )}
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center justify-between mb-3 text-sm text-merced-gray-500">
          <span>
            Showing{" "}
            <strong className="text-merced-gray-700">{paginated.length}</strong> of{" "}
            <strong className="text-merced-gray-700">{filtered.length}</strong>{" "}
            documents
            {filtered.length !== documents.length &&
              ` (${documents.length} total)`}
          </span>
          <span>
            Page <strong className="text-merced-gray-700">{page}</strong> of{" "}
            <strong className="text-merced-gray-700">{totalPages}</strong>
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-merced-gray-200">
            <div className="animate-spin h-10 w-10 border-4 border-merced-blue border-t-transparent rounded-full mx-auto" />
            <p className="text-merced-gray-500 mt-4">Loading documents…</p>
          </div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-merced-gray-200">
            <p className="text-merced-gray-500">
              No documents match the current filters.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-merced-gray-200 overflow-hidden">
            <div className="w-full">
              <table className="w-full table-fixed divide-y divide-merced-gray-200">
                <colgroup>
                  <col className="w-[13%]" /> {/* Document Name */}
                  <col className="w-[8%]" /> {/* Contract # */}
                  <col className="w-[11%]" /> {/* Outside Agency */}
                  <col className="w-[13%]" /> {/* Subject / Location */}
                  <col className="w-[9%]" /> {/* Document Type */}
                  <col className="w-[6%]" /> {/* Calendar Year */}
                  <col className="w-[7%]" /> {/* Confidence */}
                  <col className="w-[5%]" /> {/* PII */}
                  <col className="w-[8%]" /> {/* Uploaded */}
                  <col className="w-[7%]" /> {/* Status */}
                  <col className="w-[13%]" /> {/* Actions */}
                </colgroup>
                <thead className="bg-merced-gray-50">
                  <tr>
                    <th
                      className={thClass}
                      onClick={() => handleSort("file_name")}
                    >
                      Document Name <SortIcon col="file_name" />
                    </th>
                    <th
                      className={thClass}
                      onClick={() => handleSort("reference_number")}
                    >
                      Contract / Resolution No. <SortIcon col="reference_number" />
                    </th>
                    <th
                      className={thClass}
                      onClick={() => handleSort("related_party")}
                    >
                      Agency / Party <SortIcon col="related_party" />
                    </th>
                    <th
                      className={thClass}
                      onClick={() => handleSort("subject_location")}
                    >
                      Subject / Location <SortIcon col="subject_location" />
                    </th>
                    <th
                      className={thClass}
                      onClick={() => handleSort("document_type")}
                    >
                      Document Type <SortIcon col="document_type" />
                    </th>
                    <th
                      className={thClass}
                      onClick={() =>
                        handleSort("document_year" as keyof AdminDocument)
                      }
                    >
                      Calendar Year{" "}
                      <SortIcon col={"document_year" as keyof AdminDocument} />
                    </th>
                    <th
                      className={thClass}
                      onClick={() => handleSort("extraction_confidence")}
                    >
                      Confidence <SortIcon col="extraction_confidence" />
                    </th>
                    <th
                      className={thClass}
                      onClick={() =>
                        handleSort("pii_detected" as keyof AdminDocument)
                      }
                    >
                      PII{" "}
                      <SortIcon col={"pii_detected" as keyof AdminDocument} />
                    </th>
                    <th
                      className={thClass}
                      onClick={() => handleSort("upload_date")}
                    >
                      Uploaded <SortIcon col="upload_date" />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-merced-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-merced-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-merced-gray-100">
                  {paginated.map((doc) => {
                    const confidence = doc.extraction_confidence;
                    const confColor =
                      confidence == null
                        ? "text-merced-gray-400"
                        : confidence >= 0.8
                          ? "text-merced-blue-600"
                          : confidence >= 0.5
                            ? "text-merced-gold-600"
                            : "text-merced-rust-600";

                    return (
                      <tr
                        key={doc.id}
                        className="hover:bg-merced-blue-50 transition-colors duration-100"
                      >
                        {/* Document Name */}
                        <td className={tdClass}>
                          <span
                            className="block truncate text-merced-gray-700"
                            title={doc.original_file_name || doc.file_name}
                          >
                            {doc.original_file_name || doc.file_name}
                          </span>
                        </td>

                        {/* Contract # */}
                        <td className={tdClass}>
                          <span className="font-semibold text-merced-blue-700">
                            {doc.reference_number || (
                              <span className="text-merced-gray-400 italic">—</span>
                            )}
                          </span>
                        </td>

                        {/* Outside Agency */}
                        <td className={tdClass}>
                          <span
                            className="block truncate"
                            title={doc.related_party}
                          >
                            {doc.related_party || (
                              <span className="text-merced-gray-400 italic">—</span>
                            )}
                          </span>
                        </td>

                        {/* Subject / Location */}
                        <td className={tdClass}>
                          <span
                            className="block truncate"
                            title={doc.subject_location}
                          >
                            {doc.subject_location || (
                              <span className="text-merced-gray-400 italic">—</span>
                            )}
                          </span>
                        </td>

                        {/* Document Type */}
                        <td className={tdClass}>
                          {doc.document_type ? (
                            <span className="badge text-xs capitalize">
                              {doc.document_type}
                            </span>
                          ) : (
                            <span className="text-merced-gray-400 italic text-xs">
                              —
                            </span>
                          )}
                        </td>

                        {/* Year */}
                        <td className={tdClass}>
                          {(doc as any).document_year || (
                            <span className="text-merced-gray-400 italic">—</span>
                          )}
                        </td>

                        {/* Confidence */}
                        <td className={`${tdClass} ${confColor} font-medium`}>
                          {confidence != null
                            ? `${(confidence * 100).toFixed(0)}%`
                            : "—"}
                        </td>

                        {/* PII */}
                        <td className={tdClass}>
                          {doc.pii_detected ? (
                            <span
                              className="badge badge-rust text-xs"
                              title={(doc.pii_entities || []).join(", ")}
                            >
                              Yes
                            </span>
                          ) : (
                            <span className="badge badge-blue text-xs">
                              None
                            </span>
                          )}
                        </td>

                        {/* Uploaded */}
                        <td
                          className={`${tdClass} text-xs text-merced-gray-500 whitespace-nowrap`}
                        >
                          {doc.upload_date
                            ? new Date(doc.upload_date).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )
                            : "—"}
                        </td>

                        {/* Status */}
                        <td className={tdClass}>
                          {doc.requires_review ? (
                            <span className="badge badge-gold text-xs">
                              Need to Review
                            </span>
                          ) : (
                            <span className="badge badge-blue text-xs">
                              Reviewed
                            </span>
                          )}
                        </td>

                        {/* Actions — fixed-width slots keep every row aligned on one line */}
                        <td className="px-2 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">

                            {/* Approve — checkmark (only when review is pending; slot reserved otherwise) */}
                            {doc.requires_review ? (
                              <button
                                onClick={() => handleApproveForPublic(doc)}
                                title="Approve for public visibility"
                                className="flex h-7 w-7 items-center justify-center rounded text-merced-blue-600 hover:bg-merced-blue-100 transition-colors"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </button>
                            ) : (
                              <span className="inline-block h-7 w-7" aria-hidden="true" />
                            )}

                            {/* Edit — pencil icon */}
                            <button
                              onClick={() => handleEditDocument(doc)}
                              title="Edit Document Metadata"
                              className="flex h-7 w-7 items-center justify-center rounded text-merced-blue hover:bg-merced-blue-100 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                                />
                              </svg>
                            </button>
                            {/* Download — streams the file from Azure Blob Storage */}
                            <button
                              onClick={() => handleDownloadDocument(doc)}
                              disabled={downloadingId === doc.id}
                              title={
                                downloadingId === doc.id
                                  ? "Download is in progress, please wait…"
                                  : "Download Document"
                              }
                              className="flex h-7 w-7 items-center justify-center rounded text-merced-blue-600 hover:bg-merced-blue-100 transition-colors disabled:opacity-60 disabled:cursor-wait"
                            >
                              {downloadingId === doc.id ? (
                                <span
                                  className="h-4 w-4 animate-spin rounded-full border-2 border-merced-blue-600 border-t-transparent"
                                  aria-hidden="true"
                                />
                              ) : (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                  />
                                </svg>
                              )}
                            </button>
                            {/* Delete — trash icon */}
                            <button
                              onClick={() => handleDeleteDocument(doc)}
                              title="Delete Document"
                              className="flex h-7 w-7 items-center justify-center rounded text-merced-rust-500 hover:bg-merced-rust-100 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0a1 1 0 00-1-1h-4a1 1 0 00-1 1m-3 0h10"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-5">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm rounded border border-merced-gray-300 disabled:opacity-40 hover:bg-merced-gray-100 transition-colors"
            >
              «
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm rounded border border-merced-gray-300 disabled:opacity-40 hover:bg-merced-gray-100 transition-colors"
            >
              ‹ Prev
            </button>

            {/* Page number buttons — show up to 7 pages around current */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2,
              )
              .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("…");
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "…" ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="px-2 text-merced-gray-400 text-sm"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setPage(item as number)}
                    className={`px-3 py-1.5 text-sm rounded border transition-colors ${page === item
                      ? "bg-merced-blue text-white border-merced-blue"
                      : "border-merced-gray-300 hover:bg-merced-gray-100"
                      }`}
                  >
                    {item}
                  </button>
                ),
              )}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm rounded border border-merced-gray-300 disabled:opacity-40 hover:bg-merced-gray-100 transition-colors"
            >
              Next ›
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm rounded border border-merced-gray-300 disabled:opacity-40 hover:bg-merced-gray-100 transition-colors"
            >
              »
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="mt-auto bg-merced-blue-700 text-white">
        <div className="h-1 w-full bg-gradient-to-r from-merced-gold via-merced-blue-500 to-merced-blue-700" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-sm text-center sm:text-left">
              © {new Date().getFullYear()} City of Merced, California. All
              rights reserved.
            </p>
            <p className="text-xs text-merced-blue-200 text-center sm:text-right">
              678 W. 18th Street, Merced, CA 95340 | {" "}
              <a
                href="https://www.cityofmerced.gov"
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline transition-colors hover:text-white"
              >
                www.cityofmerced.gov
              </a>
              {" "} |{" "}
              <a
                href="tel:+12093888650"
                className="link-underline transition-colors hover:text-white"
              >
                Tel: (209) 388-8650
              </a>{" "}
            </p>
          </div>
        </div>
      </footer>

      {/* Approve-for-public confirmation modal */}
      {approveTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-lg max-w-md w-full shadow-2xl animate-scale-in">
            <div className="card-header flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-merced-blue-100 text-merced-blue-700">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </span>
              <h2 className="text-lg font-heading text-merced-blue-700">
                Approve for Public Visibility
              </h2>
            </div>

            <div className="card-body space-y-4">
              <p className="text-sm text-merced-gray-700">
                Are you sure you want to Approve{" "}
                <span className="font-semibold text-merced-blue-700">
                  "{approveTarget.original_file_name || approveTarget.file_name}"
                </span>{" "}
                for public visibility?
              </p>
              <p className="text-xs text-merced-gray-500">
                This will make it searchable on the public portal.
              </p>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={confirmApprove}
                  disabled={approving}
                  className="flex-1 btn-primary text-sm disabled:opacity-60"
                >
                  {approving ? "Approving…" : "Approve"}
                </button>
                <button
                  type="button"
                  onClick={() => setApproveTarget(null)}
                  disabled={approving}
                  className="flex-1 btn-outline text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete-document confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-lg max-w-md w-full shadow-2xl animate-scale-in">
            <div className="card-header flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-merced-rust-100 text-merced-rust-600">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0a1 1 0 00-1-1h-4a1 1 0 00-1 1m-3 0h10"
                  />
                </svg>
              </span>
              <h2 className="text-lg font-heading text-merced-rust-600">
                Delete Document
              </h2>
            </div>

            <div className="card-body space-y-4">
              <p className="text-sm text-merced-gray-700">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-merced-gray-900">
                  "{deleteTarget.original_file_name || deleteTarget.file_name}"
                </span>
                ?
              </p>
              <p className="text-xs text-merced-rust-600">
                This action cannot be undone.
              </p>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="flex-1 btn-danger text-sm disabled:opacity-60"
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="flex-1 btn-outline text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rate-limit Configuration Modal */}
      {showConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-lg max-w-md w-full shadow-2xl animate-scale-in">
            <div className="card-header flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-merced-blue-100 text-merced-blue-700">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </span>
                <h2 className="text-lg font-heading text-merced-blue-700">
                  Configuration
                </h2>
              </div>
              <button
                onClick={() => setShowConfig(false)}
                className="text-merced-gray-400 hover:text-merced-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>

            <div className="card-body space-y-4">
              {configLoading ? (
                <div className="py-8 text-center">
                  <div className="animate-spin h-8 w-8 border-4 border-merced-blue border-t-transparent rounded-full mx-auto" />
                  <p className="text-merced-gray-500 mt-3 text-sm">
                    Loading configuration…
                  </p>
                </div>
              ) : (
                <>
                  {/* ── Accordion: Search Rate Limit ─────────────────────── */}
                  <div className="rounded-lg border border-merced-gray-400 overflow-hidden">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenSection((s) => (s === "rate" ? null : "rate"))
                      }
                      aria-expanded={openSection === "rate" ? "true" : "false"}
                      className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-merced-gray-50"
                    >
                      <span className="text-sm font-semibold uppercase tracking-wider text-merced-gray-600">
                        Search Rate Limit
                      </span>
                      <svg
                        className={`h-4 w-4 text-merced-gray-400 transition-transform duration-200 ${openSection === "rate" ? "rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {openSection === "rate" && (
                      <div className="space-y-4 border-t border-merced-gray-500 px-4 py-4">
                        <p className="text-sm text-merced-gray-600">
                          Set the maximum number of AI searches allowed per hour.
                          This limit is shared by all public users and takes
                          effect on the next search immediately.
                        </p>

                        {/* Live usage snapshot */}
                        {config && (
                          <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-lg border border-merced-gray-200 bg-merced-gray-50 p-3">
                              <p className="text-xs uppercase tracking-wider text-merced-gray-500">
                                Used this hour
                              </p>
                              <p className="text-lg font-semibold text-merced-blue-700">
                                {config.current_count}
                                <span className="text-sm font-normal text-merced-gray-400">
                                  {" "}
                                  / {config.max_requests}
                                </span>
                              </p>
                            </div>
                            <div className="rounded-lg border border-merced-gray-200 bg-merced-gray-50 p-3">
                              <p className="text-xs uppercase tracking-wider text-merced-gray-500">
                                Remaining
                              </p>
                              <p className="text-lg font-semibold text-merced-blue-700">
                                {config.remaining_requests}
                              </p>
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            Requests per hour
                          </label>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={configInput}
                            onChange={(e) => {
                              setConfigInput(e.target.value);
                              setConfigSaved(false);
                              setConfigError(null);
                            }}
                            className="input-field text-sm"
                            placeholder="e.g. 100"
                          />
                        </div>

                        {config && !config.configured && (
                          <p className="text-xs text-merced-gold-600">
                            ⚠ The rate-limit store (Cosmos DB) is not available in
                            this environment; changes cannot be saved right now.
                          </p>
                        )}

                        {configError && (
                          <p className="text-sm text-merced-rust-600">
                            {configError}
                          </p>
                        )}
                        {configSaved && !configError && (
                          <p className="text-sm text-merced-blue-600">
                            ✓ Rate limit updated. It is now live across the
                            application.
                          </p>
                        )}

                        <button
                          type="button"
                          onClick={handleSaveConfig}
                          disabled={configSaving || config?.configured === false}
                          className="w-full btn-primary text-sm disabled:opacity-60"
                        >
                          {configSaving ? "Saving…" : "Save Rate Limit"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* ── Accordion: Extraction Confidence Threshold ───────── */}
                  <div className="rounded-lg border border-merced-gray-400 overflow-hidden">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenSection((s) =>
                          s === "confidence" ? null : "confidence",
                        )
                      }
                      aria-expanded={
                        openSection === "confidence" ? "true" : "false"
                      }
                      className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-merced-gray-50"
                    >
                      <span className="text-sm font-semibold uppercase tracking-wider text-merced-gray-600">
                        Extraction Confidence Threshold
                      </span>
                      <svg
                        className={`h-4 w-4 text-merced-gray-400 transition-transform duration-200 ${openSection === "confidence" ? "rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {openSection === "confidence" && (
                      <div className="space-y-4 border-t border-merced-gray-200 px-4 py-4">
                        <p className="text-sm text-merced-gray-600">
                          Documents extracted with a confidence below this
                          threshold are automatically flagged for admin review on
                          upload. Raising it routes more documents through manual
                          review; lowering it lets more publish automatically.
                        </p>

                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            Minimum confidence (%)
                          </label>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={confidenceInput}
                            onChange={(e) => {
                              setConfidenceInput(e.target.value);
                              setConfidenceSaved(false);
                              setConfidenceError(null);
                            }}
                            className="input-field text-sm"
                            placeholder="e.g. 90"
                          />
                          <p className="mt-1 text-xs text-merced-gray-400">
                            {confidenceScore != null
                              ? `Current threshold: ${Math.round(confidenceScore * 100)}%`
                              : "No threshold configured yet."}
                          </p>
                        </div>

                        {confidenceError && (
                          <p className="text-sm text-merced-rust-600">
                            {confidenceError}
                          </p>
                        )}
                        {confidenceSaved && !confidenceError && (
                          <p className="text-sm text-merced-blue-600">
                            ✓ Confidence threshold updated. It applies to the next
                            document upload.
                          </p>
                        )}

                        <button
                          type="button"
                          onClick={handleSaveConfidence}
                          disabled={confidenceSaving}
                          className="w-full btn-primary text-sm disabled:opacity-60"
                        >
                          {confidenceSaving
                            ? "Saving…"
                            : "Save Confidence Threshold"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* ── Accordion: Search Result Limit ───────────────────── */}
                  <div className="rounded-lg border border-merced-gray-400 overflow-hidden">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenSection((s) =>
                          s === "searchLimit" ? null : "searchLimit",
                        )
                      }
                      aria-expanded={
                        openSection === "searchLimit" ? "true" : "false"
                      }
                      className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-merced-gray-50"
                    >
                      <span className="text-sm font-semibold uppercase tracking-wider text-merced-gray-600">
                        Search Result Limit
                      </span>
                      <svg
                        className={`h-4 w-4 text-merced-gray-400 transition-transform duration-200 ${openSection === "searchLimit" ? "rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {openSection === "searchLimit" && (
                      <div className="space-y-4 border-t border-merced-gray-200 px-4 py-4">
                        <p className="text-sm text-merced-gray-600">
                          The maximum number of results a public keyword or
                          location search will return, ordered by relevance.
                          Lowering it surfaces only the strongest matches;
                          raising it returns a longer tail of weaker ones.
                        </p>

                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            Maximum results per search
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={10000}
                            step={1}
                            value={searchLimitInput}
                            onChange={(e) => {
                              setSearchLimitInput(e.target.value);
                              setSearchLimitSaved(false);
                              setSearchLimitError(null);
                            }}
                            className="input-field text-sm"
                            placeholder="e.g. 50"
                          />
                          <p className="mt-1 text-xs text-merced-gray-400">
                            {searchLimit == null
                              ? "Could not load the current limit."
                              : searchLimit.configured
                                ? `Current limit: ${searchLimit.max_results} results`
                                : `Default of ${searchLimit.max_results} results in effect — no value saved yet.`}
                          </p>
                        </div>

                        {searchLimitError && (
                          <p className="text-sm text-merced-rust-600">
                            {searchLimitError}
                          </p>
                        )}
                        {searchLimitSaved && !searchLimitError && (
                          <p className="text-sm text-merced-blue-600">
                            ✓ Search result limit updated. It applies to the next
                            search.
                          </p>
                        )}

                        <button
                          type="button"
                          onClick={handleSaveSearchLimit}
                          disabled={searchLimitSaving}
                          className="w-full btn-primary text-sm disabled:opacity-60"
                        >
                          {searchLimitSaving
                            ? "Saving…"
                            : "Save Search Result Limit"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Shared close action for the whole configuration modal */}
                  <div className="pt-1">
                    <button
                      onClick={() => setShowConfig(false)}
                      disabled={configSaving || confidenceSaving || searchLimitSaving}
                      className="w-full btn-outline text-sm"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editMode && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-lg max-w-xl w-full shadow-2xl animate-scale-in">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-heading text-merced-blue-700">
                Edit Document Metadata
              </h2>
              <button
                onClick={() => {
                  setEditMode(false);
                  setSelectedDoc(null);
                }}
                className="text-merced-gray-400 hover:text-merced-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>

            <div className="card-body space-y-4">
              {/* Read-only info */}
              <div className="bg-merced-gray-50 rounded p-3 text-sm text-merced-gray-600 space-y-1">
                <div>
                  <span className="font-medium">File:</span>{" "}
                  {selectedDoc.original_file_name || selectedDoc.file_name}
                </div>
                {selectedDoc.extraction_confidence != null && (
                  <div>
                    <span className="font-medium">Confidence:</span>{" "}
                    {(selectedDoc.extraction_confidence * 100).toFixed(0)}%
                  </div>
                )}
                {selectedDoc.pii_detected && (
                  <div className="text-merced-rust-600">
                    <span className="font-medium">⚠ PII Detected:</span>{" "}
                    {(selectedDoc.pii_entities || []).join(", ") || "Yes"}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Contract / Resolution No.
                </label>
                <input
                  type="text"
                  value={editData.reference_number || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      reference_number: e.target.value,
                    })
                  }
                  className="input-field text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Document Type
                </label>
                <Select
                  value={editData.document_type || ""}
                  onChange={(value) =>
                    setEditData({
                      ...editData,
                      document_type: value || undefined,
                    })
                  }
                  options={DOCUMENT_TYPE_OPTIONS}
                  placeholder="— Select type —"
                  ariaLabel="Document Type"
                  className="w-full text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">Calendar Year</label>
                <input
                  type="number"
                  min="1970"
                  max={new Date().getFullYear() + 1}
                  value={editData.document_year ?? ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      document_year: e.target.value
                        ? parseInt(e.target.value, 10)
                        : undefined,
                    })
                  }
                  placeholder="e.g. 2024"
                  className="input-field text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Agency / Party
                </label>
                <input
                  type="text"
                  value={editData.related_party || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, related_party: e.target.value })
                  }
                  className="input-field text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Subject / Location
                </label>
                <input
                  type="text"
                  value={editData.subject_location || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      subject_location: e.target.value,
                    })
                  }
                  className="input-field text-sm"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 btn-primary text-sm"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setSelectedDoc(null);
                  }}
                  className="flex-1 btn-outline text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
